import type { CharacterIPMedia } from '../../components/CharacterIPPanel/types';
import { getMyIpMediaReferences, getMyIpMediaReferenceUrl } from './mediaReferences';
import { getMyIpMediaUrl } from './mediaUrl';
import { getMyIpTimbreMedia } from './timbreMedia';
import type { MyIpItem, MyIpProfile } from './types';

/** 正式音色列表的原始身份；素材中台 id 和 voice_id 均不是 IP asset_id。 */
export interface MyIpVoicePreset extends Record<string, unknown> {
  id: number | string;
  voice_id: number;
  channel: string;
  ext_voice_id: string;
  name?: string;
  default_name?: string;
  trial_file: Record<string, unknown> & { url: string; };
}

/** 接受请求层无损解析的 uint64 字符串 ID，完整可试听身份及未知字段保留原文。 */
export function getMyIpVoicePreset(value: unknown): MyIpVoicePreset | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const preset = value as MyIpVoicePreset;
  const id = typeof preset.id === 'number' && Number.isSafeInteger(preset.id) ? String(preset.id) : preset.id;
  if (typeof id !== 'string' || !/^[1-9]\d*$/.test(id) || id.length > 20
    || (id.length === 20 && id > '18446744073709551615')) return undefined;
  if (!Number.isSafeInteger(preset.voice_id) || preset.voice_id <= 0
    || typeof preset.channel !== 'string' || !preset.channel || typeof preset.ext_voice_id !== 'string'
    || (preset.name != null && typeof preset.name !== 'string') || (preset.default_name != null && typeof preset.default_name !== 'string')
    || ![preset.name, preset.default_name].some((name) => typeof name === 'string' && name.trim())
    || !preset.trial_file || typeof preset.trial_file !== 'object' || Array.isArray(preset.trial_file)
    || !getMyIpMediaUrl(preset.trial_file.url)) return undefined;
  return preset;
}

/** 数字与字符串形式的同一 ID 共用列表身份，原始 ID 不转为 number。 */
export function getMyIpVoicePresetKey(preset: MyIpVoicePreset): string {
  return JSON.stringify([preset.channel, preset.voice_id, preset.ext_voice_id, String(preset.id)]);
}

/** 展示优先使用服务端本地化名称，空白名称回落默认名；原始字段不改写。 */
export function getMyIpVoicePresetName(preset: MyIpVoicePreset): string {
  return preset.name?.trim() ? preset.name : preset.default_name || '';
}

/** UI 媒体同时携带原始音色身份，试听 URL 不被当作生成结果或资产身份。 */
export function getMyIpVoicePresetMedia(preset: MyIpVoicePreset): CharacterIPMedia {
  return {
    id: getMyIpVoicePresetKey(preset),
    kind: 'audio',
    url: preset.trial_file.url,
    name: getMyIpVoicePresetName(preset),
    voicePreset: preset,
    voiceId: String(preset.voice_id),
  };
}

/** 自定义音频只接受持久化 HTTP(S) 地址，不能把本地文件或 blob 当作已保存音色。 */
function getUploadedVoice(value: unknown): CharacterIPMedia | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const media = value as Record<string, unknown>;
  const url = getMyIpMediaUrl(media.url);
  if (media.type !== 'audio' || !url) return null;
  return {
    id: url,
    kind: 'audio',
    url,
    name: typeof media.name === 'string' ? media.name : '',
    ...(typeof media.format === 'string' ? { format: media.format } : {}),
  };
}

/** 音色只读取正式 voice 资源槽，显式清除不能回落 profile 里的旧音频。 */
export function getMyIpSelectedVoice(item: MyIpItem): CharacterIPMedia | null {
  const reference = getMyIpMediaReferences(item).references.find((entry) => entry.role === 'voice' && entry.type === 'audio');
  const url = reference && getMyIpMediaReferenceUrl(reference);
  if (!reference || !url) return null;
  // 当前 URL 是唯一播放真源，旧预设元信息仅可由选择器按试听 URL 匹配回显。
  return {
    id: url,
    kind: 'audio',
    url,
    name: reference.name || '',
    ...(reference.assetId ? { assetId: reference.assetId } : {}),
    ...(reference.watermarkUrl ? { watermarkUrl: reference.watermarkUrl } : {}),
    ...(reference.duration === undefined ? {} : { duration: reference.duration }),
    ...(reference.voiceId ? { voiceId: reference.voiceId } : {}),
  };
}

/** 完整 Agent 摘要不拥有用户预设覆盖层，包括未来版本的原文；没有预设时不自动创建。 */
export function preserveMyIpVoiceSelection(base: MyIpProfile, next: MyIpProfile): MyIpProfile {
  const { voice_selection: ignoredSelection, ...profile } = next;
  if (!Object.prototype.hasOwnProperty.call(base, 'voice_selection')) return profile;
  const state = base.voice_selection as Record<string, unknown> | null;
  const selected = state?.schema_version === 1 ? state.preset : undefined;
  let voice: MyIpProfile['voice'] = selected === null ? null : getMyIpTimbreMedia(selected)?.timbre;
  if (state?.schema_version === 1 && getUploadedVoice(state.media)) voice = state.media;
  const originalVoice = Object.hasOwn(base, 'voice') ? { voice: base.voice } : {};
  return {
    ...profile,
    voice_selection: base.voice_selection,
    // 用户覆盖同步保留既有 voice 存储字段；未知版本原文继续保留。
    ...(voice !== undefined ? { voice } : originalVoice),
  };
}

/** 只保存用户确认的实际音色变化，保留完整列表项；未知 schema 不可被旧客户端覆盖。 */
export function mergeMyIpVoiceEdit(profile: MyIpProfile, next?: CharacterIPMedia | null, initial?: CharacterIPMedia | null): MyIpProfile {
  if (JSON.stringify(next ?? null) === JSON.stringify(initial ?? null)) return profile;
  const state = profile.voice_selection as Record<string, unknown> | undefined;
  if (state != null && (typeof state !== 'object' || Array.isArray(state) || state.schema_version !== 1)) {
    throw new Error('Unsupported IP voice selection state');
  }
  const preset = next ? getMyIpVoicePreset(next.voicePreset) : null;
  const timbre = next ? getMyIpTimbreMedia(next.timbre) : null;
  if (next && !next.voicePreset && !next.timbre && next.kind === 'audio' && next.id === next.url && !next.file && getMyIpMediaUrl(next.url)) {
    const media = {
      type: 'audio', url: next.url, name: next.name, format: next.format,
    };
    return {
      ...profile,
      voice: media,
      voice_selection: {
        ...state, schema_version: 1, preset: null, media,
      },
    };
  }
  if (next && (next.kind !== 'audio' || (preset
    ? next.url !== preset.trial_file.url || next.id !== getMyIpVoicePresetKey(preset)
    : !timbre || next.url !== timbre.url || next.id !== timbre.id))) {
    throw new Error('Invalid IP voice selection');
  }
  const selected = preset || (timbre ? next!.timbre : null);
  const { media: ignoredMedia, ...presetState } = state || {};
  return {
    ...profile,
    // 兼容既有 profile.voice 存储；生成消费映射仍待正式约定，音色 ID 不作为资源 asset_id。
    voice: selected ? getMyIpTimbreMedia(selected)!.timbre : null,
    voice_selection: { ...presetState, schema_version: 1, preset: selected ? JSON.parse(JSON.stringify(selected)) : null },
  };
}
